import { z } from "zod";

// Config the BACKEND owns and consumes itself. Unlike the rest of
// contracts/config, none of this is pinned onto a queue message: these
// namespaces tune backend read paths and the backend's own operational loops,
// so nothing ships them to a worker. Same rules otherwise — `.strict()`, every
// field carrying the `.default(...)` that documents its baseline.

// Read-model bounds for the dashboard's run surfaces. These are page sizes,
// not job tuning, but they are exactly the kind of number that ends up as a
// magic constant in a query, so they live here with the rest.
export const CrawlReadConfigSchema = z
  .object({
    // Frontier rows per page of run detail; the response carries a cursor for
    // the next page rather than a truncation flag.
    pageLimit: z.number().int().positive().default(200),
    // Raw agent findings on the run detail, newest first.
    findingLimit: z.number().int().positive().default(200),
    // Runs on the run list.
    runListLimit: z.number().int().positive().default(50),
  })
  .strict();
export type CrawlReadConfig = z.infer<typeof CrawlReadConfigSchema>;

export const BackendOperationsConfigSchema = z
  .object({
    sweepIntervalMs: z.number().int().positive().default(30_000),
    sourceUploadMaxBytes: z
      .number()
      .int()
      .positive()
      .default(25 * 1024 * 1024),
    sourceReadUrlSeconds: z.number().int().positive().max(604_800).default(900),
  })
  .strict();
export type BackendOperationsConfig = z.infer<
  typeof BackendOperationsConfigSchema
>;

/**
 * Bounds for the issue workbook export. A website's issue history has no
 * natural ceiling, so each sheet is capped and an over-cap export fails with
 * `export_too_large` rather than handing a customer a workbook that looks
 * complete but silently stops. The defaults sit far above any real site — they
 * exist so the read is bounded by construction, not to ration exports.
 */
export const IssueExportConfigSchema = z
  .object({
    maxIssues: z.number().int().positive().default(20_000),
    maxAffectedPages: z.number().int().positive().default(100_000),
    maxAffectedPageRows: z.number().int().positive().default(100_000),
    maxDetailBlocks: z.number().int().positive().default(200_000),
    maxDetailRows: z.number().int().positive().default(200_000),
    maxWorkbookRows: z.number().int().positive().default(200_000),
    maxWorkbookTextCharacters: z.number().int().positive().default(10_000_000),
    maxWorkbookBytes: z.number().int().positive().default(50_000_000),
    // Characters of a note or recommendation kept in the Issues sheet's summary
    // columns; the untruncated text is always on the Details sheet.
    textPreviewChars: z.number().int().positive().max(32_000).default(500),
  })
  .strict();
export type IssueExportConfig = z.infer<typeof IssueExportConfigSchema>;

/**
 * Dead-letter watch. A dead-lettered message currently fails safely and
 * silently — the incident that motivated all of this was found from a DB row
 * count, not from an alert — so the backend polls every `.dead` queue's depth
 * and reports it.
 *
 * Observation is passive (a `checkQueue` that never declares) and per-queue
 * counters only; message bodies are never scanned.
 */
export const DeadLetterMonitorConfigSchema = z
  .object({
    // Depth at or above which a queue counts as alerting. 1 = tell me about the
    // first dead message, which is the point.
    alertThreshold: z.number().int().positive().default(1),
    // Minimum gap between observations. The shared operational tick drives this,
    // so values below its configured cadence are floored by that cadence.
    pollIntervalMs: z.number().int().positive().default(30_000),
    // Whether a queue that stays dirty keeps alerting. Off by default: the log
    // line and the exception fire on the nothing -> something transition, and a
    // poll that re-fires an identical alert every tick is how the next real one
    // gets missed. Turn on while actively draining a queue.
    repeatAlerts: z.boolean().default(false),
  })
  .strict();
export type DeadLetterMonitorConfig = z.infer<
  typeof DeadLetterMonitorConfigSchema
>;
